export type Language = 'en' | 'kn';

export const SUPPORTED_LANGUAGES: { code: Language; label: string; nativeLabel: string }[] = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'kn', label: 'Kannada', nativeLabel: 'ಕನ್ನಡ' },
];

const translations = {
  en: {
    // Nav
    nav: {
      about: 'About',
      programs: 'Programs',
      impact: 'Impact',
      events: 'Events',
      news: 'News',
      contact: 'Contact',
      volunteer: 'Volunteer',
      donateNow: 'Donate Now',
      topBar: 'Arivu Foundation — Education & Social Service',
      location: 'Bengaluru, Karnataka 560073',
    },
    // Home
    home: {
      badge: 'Bengaluru-based Education & Social Trust',
      heroTitle1: 'Transforming Lives',
      heroTitle2: 'Through Education',
      heroTitle3: '& Action',
      heroSubtitle: 'Arivu Foundation empowers underprivileged communities in Bengaluru through quality education, scholarships, health awareness, and livelihood programs.',
      donateBtn: 'Donate Now — Make a Difference',
      ourStory: 'Our Story →',
      badge12a: '12A Certified',
      badge80g: '80G Tax Exempt',
      badgeTrust: 'Registered Trust',
      impactTitle: 'Our Impact in Numbers',
      impactSubtitle: 'Real change, measurable outcomes — every rupee counts.',
      programsTitle: 'Our Programs',
      programsSubtitle: 'Five pillars of change — education, scholarships, teacher training, health, and empowerment.',
      learnMore: 'Learn More →',
      fundTitle: 'How We Use Your Donations',
      fundSubtitle: 'Every rupee is accounted for. Here\'s how we allocate funds across our programs.',
      testimonialTitle: 'Voices of Impact',
      testimonialSubtitle: 'Stories from the communities we serve.',
      ctaTitle: 'Ready to Make a Difference?',
      ctaSubtitle: 'Join thousands of donors, volunteers, and partners who are transforming lives across Bengaluru.',
      ctaDonate: 'Donate Now',
      ctaVolunteer: 'Become a Volunteer',
      stats: {
        students: 'Students Educated',
        scholarships: 'Scholarships Awarded',
        teachers: 'Teachers Trained',
        communities: 'Communities Served',
      },
    },
    // About
    about: {
      badge: 'About Arivu Foundation',
      heroTitle: 'Rooted in Community,',
      heroHighlight: 'Driven by Purpose',
      heroSubtitle: 'Since 2010, Arivu Foundation has been a trusted partner in education and social development for Bengaluru\'s most vulnerable communities.',
      missionTitle: 'Our Mission',
      missionText: 'To provide equitable access to quality education and social support for underprivileged communities in Karnataka, fostering self-reliance, dignity, and sustainable development through inclusive programs.',
      visionTitle: 'Our Vision',
      visionText: 'A Karnataka where every child has access to quality education, every family has a dignified livelihood, and every community thrives with health and opportunity.',
      historyTitle: 'Our Journey',
      teamTitle: 'Our Team',
      certTitle: 'Certifications & Compliance',
      certSubtitle: 'Registered, certified, and compliant with all Indian trust regulations.',
    },
    // Programs
    programs: {
      badge: 'Our Programs',
      heroTitle: 'Five Pillars of',
      heroHighlight: 'Community Change',
      heroSubtitle: 'From education to health, our programs address the root causes of poverty and inequality in Bengaluru.',
      learnMore: 'Learn More',
      keyActivities: 'Key Activities',
      impactMetrics: 'Impact Metrics',
      ctaTitle: 'Support Our Programs',
      ctaSubtitle: 'Your donation directly funds these programs and transforms lives.',
      donateBtn: 'Donate to a Program',
      volunteerBtn: 'Volunteer With Us',
    },
    // Impact
    impact: {
      badge: 'Our Impact',
      heroTitle: 'Measuring What',
      heroHighlight: 'Matters Most',
      heroSubtitle: 'Transparent, data-driven impact reporting across all our programs.',
      overallTitle: 'Overall Impact',
      projectsTitle: 'Our Projects',
      projectsSubtitle: 'Detailed outcomes from each of our flagship initiatives.',
      outcomes: 'Key Outcomes',
      downloadReport: 'Download Report',
      ctaTitle: 'Be Part of the Impact',
      ctaSubtitle: 'Every donation, every volunteer hour, every partnership creates measurable change.',
      donateBtn: 'Donate Now',
      volunteerBtn: 'Volunteer',
    },
    // Events
    events: {
      badge: 'Events & Programs',
      heroTitle: 'Events &',
      heroHighlight: 'Programs',
      heroSubtitle: 'Join us at our upcoming events — from education summits to community health camps.',
      seatsLeft: 'seats left',
      almostFull: 'Almost Full',
      register: 'Register Now',
      ctaTitle: 'Stay Updated',
      ctaSubtitle: 'Subscribe to our newsletter for event updates and program news.',
      subscribe: 'Subscribe',
      emailPlaceholder: 'Your email address',
    },
    // News
    news: {
      badge: 'News & Stories',
      heroTitle: 'News &',
      heroHighlight: 'Stories',
      heroSubtitle: 'Real stories of impact, program updates, and insights from the field.',
      readMore: 'Read More →',
      featuredTitle: 'Featured Story',
      allStories: 'All Stories',
      ctaTitle: 'Share Our Stories',
      ctaSubtitle: 'Help us spread the word about our work and impact.',
      followUs: 'Follow Us',
    },
    // Donate
    donate: {
      badge: 'Make a Donation',
      heroTitle: 'Your Donation',
      heroHighlight: 'Changes Lives',
      heroSubtitle: 'Every rupee you donate goes directly to our programs. 80G tax exemption available.',
      chooseAmount: 'Choose Amount',
      customAmount: 'Custom Amount',
      customPlaceholder: 'Enter amount in ₹',
      frequency: 'Donation Frequency',
      oneTime: 'One-time',
      monthly: 'Monthly',
      yearly: 'Yearly',
      program: 'Donate To',
      general: 'General Fund',
      personalDetails: 'Your Details',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email Address',
      phone: 'Mobile Number',
      pan: 'PAN Number (for 80G receipt)',
      panOptional: 'Optional',
      message: 'Message (Optional)',
      messagePlaceholder: 'Any message for the team...',
      receiptRequired: 'I need an 80G donation receipt',
      submitBtn: 'Proceed to Payment',
      successTitle: 'Thank You for Your Generosity!',
      successMsg: 'Your donation request has been received. Our team will contact you with payment details and your 80G receipt.',
      bankTransfer: 'Bank Transfer Details',
      upiTitle: 'UPI Payment',
      upiId: 'UPI ID',
    },
    // Get Involved
    getInvolved: {
      badge: 'Get Involved',
      heroTitle: 'Get',
      heroHighlight: 'Involved',
      heroSubtitle: 'Join 500+ volunteers and partners who are transforming lives across Bengaluru.',
      waysTitle: 'Ways to Contribute',
      volunteerTitle: 'Volunteer With Us',
      volunteerSubtitle: 'Fill in the form below and our team will reach out within 48 hours.',
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      city: 'City',
      skills: 'Your Skills',
      availability: 'Availability',
      interest: 'Area of Interest',
      message: 'Message',
      submitBtn: 'Submit Application',
      successTitle: 'Application Received!',
      successMsg: 'Thank you for your interest in volunteering. Our team will contact you within 48 hours.',
    },
    // Contact
    contact: {
      heroTitle: 'Contact',
      heroHighlight: 'Us',
      heroSubtitle: "We'd love to hear from you — whether you're a donor, volunteer, partner, or beneficiary.",
      getInTouch: 'Get in Touch',
      address: 'Address',
      phone: 'Phone',
      emailLabel: 'Email',
      officeHours: 'Office Hours',
      sendMessage: 'Send Us a Message',
      name: 'Your Name',
      email: 'Email Address',
      phoneLabel: 'Phone (Optional)',
      subject: 'Subject',
      message: 'Your Message',
      submitBtn: 'Send Message',
      successTitle: 'Message Sent!',
      successMsg: 'Thank you for reaching out. We\'ll respond within 1–2 business days.',
    },
    // Resources
    resources: {
      heroTitle: 'Resources &',
      heroHighlight: 'Reports',
      heroSubtitle: 'Transparent reporting, downloadable resources, and financial disclosures for all stakeholders.',
      annualReports: 'Annual Reports',
      downloadsTitle: 'Downloads & Resources',
      download: 'Download',
    },
    // Legal
    legal: {
      heroTitle: 'Legal &',
      heroHighlight: 'Policies',
      heroSubtitle: 'Privacy Policy · Terms of Use · Donation Policy',
      privacyTitle: 'Privacy Policy',
      termsTitle: 'Terms of Use',
      donationPolicyTitle: 'Donation Policy',
    },
    // Footer
    footer: {
      tagline: 'Transforming lives through education, health, and community empowerment since 2010.',
      quickLinks: 'Quick Links',
      programs: 'Programs',
      contact: 'Contact',
      legal: 'Legal',
      newsletter: 'Stay Updated',
      newsletterDesc: 'Subscribe for impact stories and program updates.',
      emailPlaceholder: 'Your email address',
      subscribe: 'Subscribe',
      rights: 'All rights reserved.',
      trust: 'Registered Public Charitable Trust',
      taxExempt: '80G Tax Exempt',
      certified: '12A Certified',
    },
  },
  kn: {
    // Nav
    nav: {
      about: 'ನಮ್ಮ ಬಗ್ಗೆ',
      programs: 'ಕಾರ್ಯಕ್ರಮಗಳು',
      impact: 'ಪ್ರಭಾವ',
      events: 'ಕಾರ್ಯಕ್ರಮಗಳು',
      news: 'ಸುದ್ದಿ',
      contact: 'ಸಂಪರ್ಕ',
      volunteer: 'ಸ್ವಯಂಸೇವಕ',
      donateNow: 'ದೇಣಿಗೆ ನೀಡಿ',
      topBar: 'ಅರಿವು ಫೌಂಡೇಶನ್ — ಶಿಕ್ಷಣ ಮತ್ತು ಸಾಮಾಜಿಕ ಸೇವೆ',
      location: 'ಬೆಂಗಳೂರು, ಕರ್ನಾಟಕ 560073',
    },
    // Home
    home: {
      badge: 'ಬೆಂಗಳೂರು ಆಧಾರಿತ ಶಿಕ್ಷಣ ಮತ್ತು ಸಾಮಾಜಿಕ ಟ್ರಸ್ಟ್',
      heroTitle1: 'ಜೀವನ ಪರಿವರ್ತನೆ',
      heroTitle2: 'ಶಿಕ್ಷಣದ ಮೂಲಕ',
      heroTitle3: 'ಮತ್ತು ಕ್ರಿಯೆ',
      heroSubtitle: 'ಅರಿವು ಫೌಂಡೇಶನ್ ಬೆಂಗಳೂರಿನ ಅಸಹಾಯಕ ಸಮುದಾಯಗಳಿಗೆ ಗುಣಮಟ್ಟದ ಶಿಕ್ಷಣ, ವಿದ್ಯಾರ್ಥಿವೇತನ, ಆರೋಗ್ಯ ಜಾಗೃತಿ ಮತ್ತು ಜೀವನೋಪಾಯ ಕಾರ್ಯಕ್ರಮಗಳ ಮೂಲಕ ಸಬಲೀಕರಣ ನೀಡುತ್ತದೆ.',
      donateBtn: 'ದೇಣಿಗೆ ನೀಡಿ — ಬದಲಾವಣೆ ತನ್ನಿ',
      ourStory: 'ನಮ್ಮ ಕಥೆ →',
      badge12a: '12A ಪ್ರಮಾಣಿತ',
      badge80g: '80G ತೆರಿಗೆ ವಿನಾಯಿತಿ',
      badgeTrust: 'ನೋಂದಾಯಿತ ಟ್ರಸ್ಟ್',
      impactTitle: 'ಸಂಖ್ಯೆಗಳಲ್ಲಿ ನಮ್ಮ ಪ್ರಭಾವ',
      impactSubtitle: 'ನಿಜವಾದ ಬದಲಾವಣೆ, ಅಳೆಯಬಹುದಾದ ಫಲಿತಾಂಶಗಳು — ಪ್ರತಿ ರೂಪಾಯಿ ಮುಖ್ಯ.',
      programsTitle: 'ನಮ್ಮ ಕಾರ್ಯಕ್ರಮಗಳು',
      programsSubtitle: 'ಬದಲಾವಣೆಯ ಐದು ಸ್ತಂಭಗಳು — ಶಿಕ್ಷಣ, ವಿದ್ಯಾರ್ಥಿವೇತನ, ಶಿಕ್ಷಕ ತರಬೇತಿ, ಆರೋಗ್ಯ ಮತ್ತು ಸಬಲೀಕರಣ.',
      learnMore: 'ಇನ್ನಷ್ಟು ತಿಳಿಯಿರಿ →',
      fundTitle: 'ನಿಮ್ಮ ದೇಣಿಗೆ ಹೇಗೆ ಬಳಸಲಾಗುತ್ತದೆ',
      fundSubtitle: 'ಪ್ರತಿ ರೂಪಾಯಿ ಲೆಕ್ಕ ಇದೆ. ನಮ್ಮ ಕಾರ್ಯಕ್ರಮಗಳಲ್ಲಿ ನಿಧಿ ಹಂಚಿಕೆ ಹೀಗಿದೆ.',
      testimonialTitle: 'ಪ್ರಭಾವದ ಧ್ವನಿಗಳು',
      testimonialSubtitle: 'ನಾವು ಸೇವೆ ಸಲ್ಲಿಸುವ ಸಮುದಾಯಗಳ ಕಥೆಗಳು.',
      ctaTitle: 'ಬದಲಾವಣೆ ತರಲು ಸಿದ್ಧರಿದ್ದೀರಾ?',
      ctaSubtitle: 'ಬೆಂಗಳೂರಿನಾದ್ಯಂತ ಜೀವನ ಪರಿವರ್ತಿಸುತ್ತಿರುವ ಸಾವಿರಾರು ದಾನಿಗಳು, ಸ್ವಯಂಸೇವಕರು ಮತ್ತು ಪಾಲುದಾರರೊಂದಿಗೆ ಸೇರಿ.',
      ctaDonate: 'ದೇಣಿಗೆ ನೀಡಿ',
      ctaVolunteer: 'ಸ್ವಯಂಸೇವಕರಾಗಿ',
      stats: {
        students: 'ಶಿಕ್ಷಣ ಪಡೆದ ವಿದ್ಯಾರ್ಥಿಗಳು',
        scholarships: 'ವಿದ್ಯಾರ್ಥಿವೇತನಗಳು',
        teachers: 'ತರಬೇತಿ ಪಡೆದ ಶಿಕ್ಷಕರು',
        communities: 'ಸೇವೆ ಸಲ್ಲಿಸಿದ ಸಮುದಾಯಗಳು',
      },
    },
    // About
    about: {
      badge: 'ಅರಿವು ಫೌಂಡೇಶನ್ ಬಗ್ಗೆ',
      heroTitle: 'ಸಮುದಾಯದಲ್ಲಿ ಬೇರೂರಿ,',
      heroHighlight: 'ಉದ್ದೇಶದಿಂದ ಪ್ರೇರಿತ',
      heroSubtitle: '2010 ರಿಂದ, ಅರಿವು ಫೌಂಡೇಶನ್ ಬೆಂಗಳೂರಿನ ಅತ್ಯಂತ ದುರ್ಬಲ ಸಮುದಾಯಗಳಿಗೆ ಶಿಕ್ಷಣ ಮತ್ತು ಸಾಮಾಜಿಕ ಅಭಿವೃದ್ಧಿಯಲ್ಲಿ ವಿಶ್ವಾಸಾರ್ಹ ಪಾಲುದಾರನಾಗಿದೆ.',
      missionTitle: 'ನಮ್ಮ ಧ್ಯೇಯ',
      missionText: 'ಕರ್ನಾಟಕದ ಅಸಹಾಯಕ ಸಮುದಾಯಗಳಿಗೆ ಗುಣಮಟ್ಟದ ಶಿಕ್ಷಣ ಮತ್ತು ಸಾಮಾಜಿಕ ಬೆಂಬಲಕ್ಕೆ ಸಮಾನ ಪ್ರವೇಶ ಒದಗಿಸುವುದು, ಸ್ವಾವಲಂಬನೆ, ಘನತೆ ಮತ್ತು ಸಮಗ್ರ ಕಾರ್ಯಕ್ರಮಗಳ ಮೂಲಕ ಸುಸ್ಥಿರ ಅಭಿವೃದ್ಧಿ ಉತ್ತೇಜಿಸುವುದು.',
      visionTitle: 'ನಮ್ಮ ದೃಷ್ಟಿ',
      visionText: 'ಪ್ರತಿ ಮಗುವಿಗೆ ಗುಣಮಟ್ಟದ ಶಿಕ್ಷಣ, ಪ್ರತಿ ಕುಟುಂಬಕ್ಕೆ ಘನ ಜೀವನೋಪಾಯ ಮತ್ತು ಪ್ರತಿ ಸಮುದಾಯ ಆರೋಗ್ಯ ಮತ್ತು ಅವಕಾಶದಿಂದ ಅಭಿವೃದ್ಧಿ ಹೊಂದುವ ಕರ್ನಾಟಕ.',
      historyTitle: 'ನಮ್ಮ ಪ್ರಯಾಣ',
      teamTitle: 'ನಮ್ಮ ತಂಡ',
      certTitle: 'ಪ್ರಮಾಣಪತ್ರಗಳು ಮತ್ತು ಅನುಸರಣೆ',
      certSubtitle: 'ಎಲ್ಲಾ ಭಾರತೀಯ ಟ್ರಸ್ಟ್ ನಿಯಮಗಳಿಗೆ ನೋಂದಾಯಿತ, ಪ್ರಮಾಣಿತ ಮತ್ತು ಅನುಸರಣೆ.',
    },
    // Programs
    programs: {
      badge: 'ನಮ್ಮ ಕಾರ್ಯಕ್ರಮಗಳು',
      heroTitle: 'ಸಮುದಾಯ ಬದಲಾವಣೆಯ',
      heroHighlight: 'ಐದು ಸ್ತಂಭಗಳು',
      heroSubtitle: 'ಶಿಕ್ಷಣದಿಂದ ಆರೋಗ್ಯದವರೆಗೆ, ನಮ್ಮ ಕಾರ್ಯಕ್ರಮಗಳು ಬೆಂಗಳೂರಿನಲ್ಲಿ ಬಡತನ ಮತ್ತು ಅಸಮಾನತೆಯ ಮೂಲ ಕಾರಣಗಳನ್ನು ಪರಿಹರಿಸುತ್ತವೆ.',
      learnMore: 'ಇನ್ನಷ್ಟು ತಿಳಿಯಿರಿ',
      keyActivities: 'ಪ್ರಮುಖ ಚಟುವಟಿಕೆಗಳು',
      impactMetrics: 'ಪ್ರಭಾವ ಮಾಪನಗಳು',
      ctaTitle: 'ನಮ್ಮ ಕಾರ್ಯಕ್ರಮಗಳನ್ನು ಬೆಂಬಲಿಸಿ',
      ctaSubtitle: 'ನಿಮ್ಮ ದೇಣಿಗೆ ನೇರವಾಗಿ ಈ ಕಾರ್ಯಕ್ರಮಗಳಿಗೆ ಹೋಗಿ ಜೀವನ ಪರಿವರ್ತಿಸುತ್ತದೆ.',
      donateBtn: 'ಕಾರ್ಯಕ್ರಮಕ್ಕೆ ದೇಣಿಗೆ ನೀಡಿ',
      volunteerBtn: 'ನಮ್ಮೊಂದಿಗೆ ಸ್ವಯಂಸೇವಕರಾಗಿ',
    },
    // Impact
    impact: {
      badge: 'ನಮ್ಮ ಪ್ರಭಾವ',
      heroTitle: 'ಮುಖ್ಯವಾದದ್ದನ್ನು',
      heroHighlight: 'ಅಳೆಯುವುದು',
      heroSubtitle: 'ನಮ್ಮ ಎಲ್ಲಾ ಕಾರ್ಯಕ್ರಮಗಳಲ್ಲಿ ಪಾರದರ್ಶಕ, ಡೇಟಾ-ಆಧಾರಿತ ಪ್ರಭಾವ ವರದಿ.',
      overallTitle: 'ಒಟ್ಟಾರೆ ಪ್ರಭಾವ',
      projectsTitle: 'ನಮ್ಮ ಯೋಜನೆಗಳು',
      projectsSubtitle: 'ನಮ್ಮ ಪ್ರತಿಯೊಂದು ಪ್ರಮುಖ ಉಪಕ್ರಮದ ವಿವರವಾದ ಫಲಿತಾಂಶಗಳು.',
      outcomes: 'ಪ್ರಮುಖ ಫಲಿತಾಂಶಗಳು',
      downloadReport: 'ವರದಿ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ',
      ctaTitle: 'ಪ್ರಭಾವದ ಭಾಗವಾಗಿ',
      ctaSubtitle: 'ಪ್ರತಿ ದೇಣಿಗೆ, ಪ್ರತಿ ಸ್ವಯಂಸೇವಕ ಗಂಟೆ, ಪ್ರತಿ ಪಾಲುದಾರಿಕೆ ಅಳೆಯಬಹುದಾದ ಬದಲಾವಣೆ ಸೃಷ್ಟಿಸುತ್ತದೆ.',
      donateBtn: 'ದೇಣಿಗೆ ನೀಡಿ',
      volunteerBtn: 'ಸ್ವಯಂಸೇವಕ',
    },
    // Events
    events: {
      badge: 'ಕಾರ್ಯಕ್ರಮಗಳು',
      heroTitle: 'ಕಾರ್ಯಕ್ರಮಗಳು ಮತ್ತು',
      heroHighlight: 'ಸಮಾರಂಭಗಳು',
      heroSubtitle: 'ನಮ್ಮ ಮುಂಬರುವ ಕಾರ್ಯಕ್ರಮಗಳಲ್ಲಿ ಭಾಗವಹಿಸಿ — ಶಿಕ್ಷಣ ಶೃಂಗಸಭೆಯಿಂದ ಸಮುದಾಯ ಆರೋಗ್ಯ ಶಿಬಿರಗಳವರೆಗೆ.',
      seatsLeft: 'ಸ್ಥಾನಗಳು ಉಳಿದಿವೆ',
      almostFull: 'ಬಹುತೇಕ ತುಂಬಿದೆ',
      register: 'ನೋಂದಾಯಿಸಿ',
      ctaTitle: 'ಅಪ್‌ಡೇಟ್ ಆಗಿರಿ',
      ctaSubtitle: 'ಕಾರ್ಯಕ್ರಮ ಅಪ್‌ಡೇಟ್‌ಗಳು ಮತ್ತು ಕಾರ್ಯಕ್ರಮ ಸುದ್ದಿಗಾಗಿ ನಮ್ಮ ನ್ಯೂಸ್‌ಲೆಟರ್‌ಗೆ ಚಂದಾದಾರರಾಗಿ.',
      subscribe: 'ಚಂದಾದಾರರಾಗಿ',
      emailPlaceholder: 'ನಿಮ್ಮ ಇಮೇಲ್ ವಿಳಾಸ',
    },
    // News
    news: {
      badge: 'ಸುದ್ದಿ ಮತ್ತು ಕಥೆಗಳು',
      heroTitle: 'ಸುದ್ದಿ ಮತ್ತು',
      heroHighlight: 'ಕಥೆಗಳು',
      heroSubtitle: 'ಪ್ರಭಾವದ ನಿಜವಾದ ಕಥೆಗಳು, ಕಾರ್ಯಕ್ರಮ ಅಪ್‌ಡೇಟ್‌ಗಳು ಮತ್ತು ಕ್ಷೇತ್ರದಿಂದ ಒಳನೋಟಗಳು.',
      readMore: 'ಇನ್ನಷ್ಟು ಓದಿ →',
      featuredTitle: 'ವಿಶೇಷ ಕಥೆ',
      allStories: 'ಎಲ್ಲಾ ಕಥೆಗಳು',
      ctaTitle: 'ನಮ್ಮ ಕಥೆಗಳನ್ನು ಹಂಚಿಕೊಳ್ಳಿ',
      ctaSubtitle: 'ನಮ್ಮ ಕೆಲಸ ಮತ್ತು ಪ್ರಭಾವದ ಬಗ್ಗೆ ಮಾತನ್ನು ಹರಡಲು ಸಹಾಯ ಮಾಡಿ.',
      followUs: 'ನಮ್ಮನ್ನು ಅನುಸರಿಸಿ',
    },
    // Donate
    donate: {
      badge: 'ದೇಣಿಗೆ ನೀಡಿ',
      heroTitle: 'ನಿಮ್ಮ ದೇಣಿಗೆ',
      heroHighlight: 'ಜೀವನ ಬದಲಿಸುತ್ತದೆ',
      heroSubtitle: 'ನೀವು ದೇಣಿಗೆ ನೀಡುವ ಪ್ರತಿ ರೂಪಾಯಿ ನೇರವಾಗಿ ನಮ್ಮ ಕಾರ್ಯಕ್ರಮಗಳಿಗೆ ಹೋಗುತ್ತದೆ. 80G ತೆರಿಗೆ ವಿನಾಯಿತಿ ಲಭ್ಯ.',
      chooseAmount: 'ಮೊತ್ತ ಆಯ್ಕೆ ಮಾಡಿ',
      customAmount: 'ಕಸ್ಟಮ್ ಮೊತ್ತ',
      customPlaceholder: '₹ ನಲ್ಲಿ ಮೊತ್ತ ನಮೂದಿಸಿ',
      frequency: 'ದೇಣಿಗೆ ಆವರ್ತನ',
      oneTime: 'ಒಂದು ಬಾರಿ',
      monthly: 'ಮಾಸಿಕ',
      yearly: 'ವಾರ್ಷಿಕ',
      program: 'ದೇಣಿಗೆ ನೀಡಿ',
      general: 'ಸಾಮಾನ್ಯ ನಿಧಿ',
      personalDetails: 'ನಿಮ್ಮ ವಿವರಗಳು',
      firstName: 'ಮೊದಲ ಹೆಸರು',
      lastName: 'ಕೊನೆಯ ಹೆಸರು',
      email: 'ಇಮೇಲ್ ವಿಳಾಸ',
      phone: 'ಮೊಬೈಲ್ ಸಂಖ್ಯೆ',
      pan: 'PAN ಸಂಖ್ಯೆ (80G ರಸೀದಿಗಾಗಿ)',
      panOptional: 'ಐಚ್ಛಿಕ',
      message: 'ಸಂದೇಶ (ಐಚ್ಛಿಕ)',
      messagePlaceholder: 'ತಂಡಕ್ಕೆ ಯಾವುದಾದರೂ ಸಂದೇಶ...',
      receiptRequired: 'ನನಗೆ 80G ದೇಣಿಗೆ ರಸೀದಿ ಬೇಕು',
      submitBtn: 'ಪಾವತಿಗೆ ಮುಂದುವರಿಯಿರಿ',
      successTitle: 'ನಿಮ್ಮ ಔದಾರ್ಯಕ್ಕೆ ಧನ್ಯವಾದಗಳು!',
      successMsg: 'ನಿಮ್ಮ ದೇಣಿಗೆ ವಿನಂತಿ ಸ್ವೀಕರಿಸಲಾಗಿದೆ. ನಮ್ಮ ತಂಡ ಪಾವತಿ ವಿವರಗಳು ಮತ್ತು ನಿಮ್ಮ 80G ರಸೀದಿಯೊಂದಿಗೆ ಸಂಪರ್ಕಿಸುತ್ತದೆ.',
      bankTransfer: 'ಬ್ಯಾಂಕ್ ವರ್ಗಾವಣೆ ವಿವರಗಳು',
      upiTitle: 'UPI ಪಾವತಿ',
      upiId: 'UPI ID',
    },
    // Get Involved
    getInvolved: {
      badge: 'ಭಾಗವಹಿಸಿ',
      heroTitle: 'ಭಾಗ',
      heroHighlight: 'ವಹಿಸಿ',
      heroSubtitle: 'ಬೆಂಗಳೂರಿನಾದ್ಯಂತ ಜೀವನ ಪರಿವರ್ತಿಸುತ್ತಿರುವ 500+ ಸ್ವಯಂಸೇವಕರು ಮತ್ತು ಪಾಲುದಾರರೊಂದಿಗೆ ಸೇರಿ.',
      waysTitle: 'ಕೊಡುಗೆ ನೀಡುವ ವಿಧಾನಗಳು',
      volunteerTitle: 'ನಮ್ಮೊಂದಿಗೆ ಸ್ವಯಂಸೇವಕರಾಗಿ',
      volunteerSubtitle: 'ಕೆಳಗಿನ ಫಾರ್ಮ್ ಭರ್ತಿ ಮಾಡಿ, ನಮ್ಮ ತಂಡ 48 ಗಂಟೆಗಳಲ್ಲಿ ಸಂಪರ್ಕಿಸುತ್ತದೆ.',
      name: 'ಪೂರ್ಣ ಹೆಸರು',
      email: 'ಇಮೇಲ್ ವಿಳಾಸ',
      phone: 'ಫೋನ್ ಸಂಖ್ಯೆ',
      city: 'ನಗರ',
      skills: 'ನಿಮ್ಮ ಕೌಶಲ್ಯಗಳು',
      availability: 'ಲಭ್ಯತೆ',
      interest: 'ಆಸಕ್ತಿಯ ಕ್ಷೇತ್ರ',
      message: 'ಸಂದೇಶ',
      submitBtn: 'ಅರ್ಜಿ ಸಲ್ಲಿಸಿ',
      successTitle: 'ಅರ್ಜಿ ಸ್ವೀಕರಿಸಲಾಗಿದೆ!',
      successMsg: 'ಸ್ವಯಂಸೇವಕರಾಗಲು ನಿಮ್ಮ ಆಸಕ್ತಿಗೆ ಧನ್ಯವಾದಗಳು. ನಮ್ಮ ತಂಡ 48 ಗಂಟೆಗಳಲ್ಲಿ ಸಂಪರ್ಕಿಸುತ್ತದೆ.',
    },
    // Contact
    contact: {
      heroTitle: 'ನಮ್ಮನ್ನು',
      heroHighlight: 'ಸಂಪರ್ಕಿಸಿ',
      heroSubtitle: 'ನೀವು ದಾನಿ, ಸ್ವಯಂಸೇವಕ, ಪಾಲುದಾರ ಅಥವಾ ಫಲಾನುಭವಿ ಆಗಿರಲಿ — ನಾವು ನಿಮ್ಮಿಂದ ಕೇಳಲು ಇಷ್ಟಪಡುತ್ತೇವೆ.',
      getInTouch: 'ಸಂಪರ್ಕ ಮಾಡಿ',
      address: 'ವಿಳಾಸ',
      phone: 'ದೂರವಾಣಿ',
      emailLabel: 'ಇಮೇಲ್',
      officeHours: 'ಕಚೇರಿ ಸಮಯ',
      sendMessage: 'ನಮಗೆ ಸಂದೇಶ ಕಳುಹಿಸಿ',
      name: 'ನಿಮ್ಮ ಹೆಸರು',
      email: 'ಇಮೇಲ್ ವಿಳಾಸ',
      phoneLabel: 'ಫೋನ್ (ಐಚ್ಛಿಕ)',
      subject: 'ವಿಷಯ',
      message: 'ನಿಮ್ಮ ಸಂದೇಶ',
      submitBtn: 'ಸಂದೇಶ ಕಳುಹಿಸಿ',
      successTitle: 'ಸಂದೇಶ ಕಳುಹಿಸಲಾಗಿದೆ!',
      successMsg: 'ಸಂಪರ್ಕಿಸಿದ್ದಕ್ಕೆ ಧನ್ಯವಾದಗಳು. ನಾವು 1–2 ವ್ಯಾಪಾರ ದಿನಗಳಲ್ಲಿ ಪ್ರತಿಕ್ರಿಯಿಸುತ್ತೇವೆ.',
    },
    // Resources
    resources: {
      heroTitle: 'ಸಂಪನ್ಮೂಲಗಳು ಮತ್ತು',
      heroHighlight: 'ವರದಿಗಳು',
      heroSubtitle: 'ಎಲ್ಲಾ ಮಧ್ಯಸ್ಥಗಾರರಿಗೆ ಪಾರದರ್ಶಕ ವರದಿ, ಡೌನ್‌ಲೋಡ್ ಮಾಡಬಹುದಾದ ಸಂಪನ್ಮೂಲಗಳು ಮತ್ತು ಆರ್ಥಿಕ ಬಹಿರಂಗಪಡಿಸುವಿಕೆ.',
      annualReports: 'ವಾರ್ಷಿಕ ವರದಿಗಳು',
      downloadsTitle: 'ಡೌನ್‌ಲೋಡ್‌ಗಳು ಮತ್ತು ಸಂಪನ್ಮೂಲಗಳು',
      download: 'ಡೌನ್‌ಲೋಡ್',
    },
    // Legal
    legal: {
      heroTitle: 'ಕಾನೂನು ಮತ್ತು',
      heroHighlight: 'ನೀತಿಗಳು',
      heroSubtitle: 'ಗೌಪ್ಯತಾ ನೀತಿ · ಬಳಕೆಯ ನಿಯಮಗಳು · ದೇಣಿಗೆ ನೀತಿ',
      privacyTitle: 'ಗೌಪ್ಯತಾ ನೀತಿ',
      termsTitle: 'ಬಳಕೆಯ ನಿಯಮಗಳು',
      donationPolicyTitle: 'ದೇಣಿಗೆ ನೀತಿ',
    },
    // Footer
    footer: {
      tagline: '2010 ರಿಂದ ಶಿಕ್ಷಣ, ಆರೋಗ್ಯ ಮತ್ತು ಸಮುದಾಯ ಸಬಲೀಕರಣದ ಮೂಲಕ ಜೀವನ ಪರಿವರ್ತನೆ.',
      quickLinks: 'ತ್ವರಿತ ಲಿಂಕ್‌ಗಳು',
      programs: 'ಕಾರ್ಯಕ್ರಮಗಳು',
      contact: 'ಸಂಪರ್ಕ',
      legal: 'ಕಾನೂನು',
      newsletter: 'ಅಪ್‌ಡೇಟ್ ಆಗಿರಿ',
      newsletterDesc: 'ಪ್ರಭಾವದ ಕಥೆಗಳು ಮತ್ತು ಕಾರ್ಯಕ್ರಮ ಅಪ್‌ಡೇಟ್‌ಗಳಿಗಾಗಿ ಚಂದಾದಾರರಾಗಿ.',
      emailPlaceholder: 'ನಿಮ್ಮ ಇಮೇಲ್ ವಿಳಾಸ',
      subscribe: 'ಚಂದಾದಾರರಾಗಿ',
      rights: 'ಎಲ್ಲಾ ಹಕ್ಕುಗಳು ಕಾಯ್ದಿರಿಸಲಾಗಿದೆ.',
      trust: 'ನೋಂದಾಯಿತ ಸಾರ್ವಜನಿಕ ದಾನಧರ್ಮ ಟ್ರಸ್ಟ್',
      taxExempt: '80G ತೆರಿಗೆ ವಿನಾಯಿತಿ',
      certified: '12A ಪ್ರಮಾಣಿತ',
    },
  },
};

export function getTranslations(language: Language) {
  return translations[language];
}
